import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { FolioToggleContext, type FolioOpenReason } from '@/hooks/folio-toggle-context';
import { isEditableTarget } from '@/lib/keyboard-shortcut-guards';

function isDisabledElement(element: HTMLElement): boolean {
  return (
    (element instanceof HTMLButtonElement ||
      element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLOptGroupElement ||
      element instanceof HTMLOptionElement ||
      element instanceof HTMLFieldSetElement) &&
    element.disabled
  );
}

function canRestoreFocus(element: HTMLElement | null): element is HTMLElement {
  if (element === null || !element.isConnected || isDisabledElement(element)) {
    return false;
  }

  if (element.closest('[inert]') !== null) {
    return false;
  }

  const { display, visibility } = window.getComputedStyle(element);
  return display !== 'none' && visibility !== 'hidden';
}

function findFallbackFocusTarget(): HTMLElement | null {
  const candidates = [
    document.querySelector<HTMLElement>('[data-folio-workspace-trigger]'),
    document.querySelector<HTMLElement>('[data-folio-trigger]'),
    document.querySelector<HTMLElement>('[aria-controls="folio-panel"]'),
    document.getElementById('main-content'),
  ];

  for (const candidate of candidates) {
    if (canRestoreFocus(candidate)) {
      return candidate;
    }
  }

  return null;
}

// Sean slides in over 200ms (the header's Sean button moves with it), and a
// person needs about another 200ms to react to something appearing. A press
// that begins within this long of Sean opening itself was aimed at the closed
// panel the user was looking at.
const UNSEEN_AFTER_OPENING_ITSELF_MS = 400;

export function FolioToggleProvider({ children }: { readonly children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [openReason, setOpenReason] = useState<FolioOpenReason>('user');
  const openerRef = useRef<HTMLElement | null>(null);
  const previousIsOpenRef = useRef(false);
  // Once the user has shown or hidden Sean, their choice stands: Sean no
  // longer opens itself over it.
  const userHasChosenRef = useRef(false);
  // When Sean opened itself, until the user next shows or hides it.
  const openedItselfAtRef = useRef<number | null>(null);

  const captureOpener = useCallback((reason: FolioOpenReason) => {
    if (reason !== 'user') {
      openerRef.current = null;
      return;
    }

    const activeElement = document.activeElement;
    if (
      !(activeElement instanceof HTMLElement) ||
      activeElement === document.body ||
      activeElement === document.documentElement ||
      activeElement.closest('#folio-panel') !== null
    ) {
      return;
    }

    openerRef.current = activeElement;
  }, []);

  const open = useCallback((reason: FolioOpenReason = 'user') => {
    if (reason === 'bootstrap') {
      if (isOpen || userHasChosenRef.current) {
        return;
      }
      captureOpener(reason);
      openedItselfAtRef.current = performance.now();
      setOpenReason(reason);
      setIsOpen(true);
      return;
    }
    // The user's open is idempotent: showing Sean when it is open keeps it
    // open and makes it theirs.
    userHasChosenRef.current = true;
    openedItselfAtRef.current = null;
    if (!isOpen) {
      captureOpener(reason);
    }
    setOpenReason(reason);
    setIsOpen(true);
  }, [captureOpener, isOpen]);
  const close = useCallback(() => {
    userHasChosenRef.current = true;
    openedItselfAtRef.current = null;
    setOpenReason('user');
    setIsOpen(false);
  }, []);
  // Acts on the panel this render drew, which is the one the user pressed on;
  // Sean opening itself under the press cannot turn Show Sean into Hide.
  const toggle = useCallback((pressedAt: number) => {
    const openedItselfAt = openedItselfAtRef.current;
    const pressedBeforeSeen =
      openedItselfAt !== null && pressedAt < openedItselfAt + UNSEEN_AFTER_OPENING_ITSELF_MS;
    if (isOpen && !pressedBeforeSeen) {
      close();
      return;
    }
    open('user');
  }, [close, isOpen, open]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        !(event.metaKey || event.ctrlKey) ||
        !event.shiftKey ||
        event.key.toLowerCase() !== 'f' ||
        isEditableTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      toggle(event.timeStamp);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle]);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (isOpen || !wasOpen) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const restoreTarget = canRestoreFocus(openerRef.current)
        ? openerRef.current
        : findFallbackFocusTarget();
      restoreTarget?.focus();
      openerRef.current = null;
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  const value = useMemo(
    () => ({ isOpen, openReason, toggle, open, close }),
    [close, isOpen, open, openReason, toggle],
  );

  return <FolioToggleContext value={value}>{children}</FolioToggleContext>;
}
